import React, { useEffect, useMemo, useState } from "react";
import { createProjectManual } from "../../projects/api";
import { listAccounts } from "../../lib/accounts";
import { Field } from "../../dashboard/primitives";
import {
  PAYMENT_PLANS,
  PROJECT_TYPES,
  TIMELINES,
  timelineLabel,
  computeQuote,
  seedMilestonesFor,
} from "../../projects/pricing";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const NewProjectForm = ({ onCreated }) => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [type, setType] = useState("web");
  const [timeline, setTimeline] = useState("6w");
  const [scopeText, setScopeText] = useState("Discovery\nDesign\nBuild\nLaunch");
  const [paymentPlan, setPaymentPlan] = useState("full");
  const [budgetOverride, setBudgetOverride] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Projects belong to an account. Create the account (and its members) under
  // Admin → Accounts first, then assign a project to it here.
  useEffect(() => {
    listAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  const scope = useMemo(
    () => scopeText.split("\n").map((s) => s.trim()).filter(Boolean),
    [scopeText]
  );

  const quote = useMemo(() => computeQuote({ type, timeline, scope }), [type, timeline, scope]);
  const effectiveBudget = budgetOverride === "" ? quote.budgetUsd : Number(budgetOverride);
  const milestonesPreview = useMemo(
    () => seedMilestonesFor({ paymentPlan, budgetUsd: effectiveBudget }),
    [paymentPlan, effectiveBudget]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const account = accounts.find((a) => a.id === accountId);
      await createProjectManual({
        accountId,
        primaryContactUid: account?.ownerUid || null,
        title,
        summary,
        type,
        budgetUsd: effectiveBudget,
        paymentPlan,
      });
      setTitle("");
      setSummary("");
      onCreated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gap: 14 }}>
      <Field label="Account">
        <select className="fw-select" value={accountId} onChange={(e) => setAccountId(e.target.value)} required>
          <option value="">Select account…</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.memberUids?.length || 0} member{(a.memberUids?.length || 0) === 1 ? "" : "s"})
            </option>
          ))}
        </select>
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Title">
          <input className="fw-input" value={title} onChange={(e) => setTitle(e.target.value)} required />
        </Field>
        <Field label="Type">
          <select className="fw-select" value={type} onChange={(e) => setType(e.target.value)}>
            {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Summary">
        <textarea className="fw-textarea" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} />
      </Field>

      <Field label="Scope items (one per line — used for quote)">
        <textarea className="fw-textarea" rows={4} value={scopeText} onChange={(e) => setScopeText(e.target.value)} />
      </Field>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Field label="Timeline">
          <select className="fw-select" value={timeline} onChange={(e) => setTimeline(e.target.value)}>
            {TIMELINES.map((t) => <option key={t} value={t}>{timelineLabel(t)}</option>)}
          </select>
        </Field>
        <Field label="Payment plan">
          <select className="fw-select" value={paymentPlan} onChange={(e) => setPaymentPlan(e.target.value)}>
            {PAYMENT_PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label={`Budget (auto: ${fmt(quote.budgetUsd)})`}>
          <input className="fw-input" type="number" placeholder={String(quote.budgetUsd)} value={budgetOverride} onChange={(e) => setBudgetOverride(e.target.value)} />
        </Field>
      </div>

      <div style={{ background: "var(--fw-cream-2)", padding: 12, borderRadius: 4, fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
        <div style={{ marginBottom: 6 }}>
          {quote.hours} hrs × {fmt(quote.rate)}/hr = <strong style={{ color: "var(--fw-ink)" }}>{fmt(effectiveBudget)}</strong> · seeds {milestonesPreview.length} milestone{milestonesPreview.length === 1 ? "" : "s"}
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {milestonesPreview.slice(0, 6).map((m, i) => (
            <span key={i} style={{ background: "var(--fw-cream)", border: "1px solid var(--fw-line)", padding: "2px 6px", borderRadius: 2 }}>
              {m.label} · {fmt(m.amount)}
            </span>
          ))}
          {milestonesPreview.length > 6 && <span>+{milestonesPreview.length - 6} more</span>}
        </div>
      </div>

      {error && <div style={{ color: "var(--fw-red)", fontSize: 13 }}>{error}</div>}

      <div>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={submitting}>
          {submitting ? "Creating…" : "Create project"}
        </button>
      </div>
    </form>
  );
};

export default NewProjectForm;
